import { AppData, Log } from "@lightningjs/sdk";
import { AppSetup } from "./app-setup";
import { Helper } from "./helper";
import { Logger } from "./logger";
import VastParser, { AdObj } from "./vast-parser";
import { BugAdsInfo } from "./app-api-config";
import childApp from "./child-app";


const DESIGN_FRAME = { width: 1920, height: 1080 };
type PlayerRect = { x: number, y: number, width: number, height: number };
const FULL_SCREEN: PlayerRect = { x: 0, y: 0, width: DESIGN_FRAME.width, height: DESIGN_FRAME.height };
const L_BAND_PLAYER: PlayerRect = { x: 470, y: 0, width: 1450, height: 815 };
const L_BAND_TRANSITION_MS = 500;
const L_BAND_TRANSITION = ['width', 'height', 'margin', 'left', 'right', 'bottom', 'max-width', 'font-size']
    .map(prop => prop + ' ' + L_BAND_TRANSITION_MS + 'ms ease-out')
    .join(', ');
const SUBTITLE_INSET = { left: 10, right: 10, bottom: 90 };
const BUG_AD_Z_INDEX = 900;

export class BugAds {
    shouldDisplayAd = false;
    adDisplayedStatus = false;
    config: BugAdsInfo | null = null;
    nextAdTime = -1;
    pendingInterval = 0;
    currentAdDuration = 0;
    currentBugAdEndTime = 0;
    adObj: AdObj | null = null;
    videoPlayer: any = null;
    currentAdPos = 0;
    currbreakPointAdMapCounter = 0;
    previousVideoId: any = null;
    startTime = -1;
    videoRuntime = 0;
    introEndTime = 0;
    lBandActive = false;
    transitionTimerId: any = null;
    subtitleFontSize: string | null = null;
    trackersStatus = {
        start: false,
        midpoint: false,
        complete: false
    };

    constructor(videoInfo: any, videoPlayer: any, previousVideoId: any = null) {
        const { appState } = AppData as AppSetup;
        const appApiConfig = appState?.getAppApiConfig();
        this.config = appApiConfig?.adsdkConfig?.bugAds || null;
        this.videoPlayer = videoPlayer;
        this.previousVideoId = previousVideoId;
        if (this.init(videoInfo)) {
            this.loadCreatives();
        }
    }

    // Pull the pre-fetched creatives from the app-level cache instead of hitting
    // the ad server per video. If the launch prefetch hasn't populated the cache
    // yet (still loading) we simply run without creatives this cycle; if it never
    // ran or errored, kick an idempotent fetch so a later video can show ads.
    loadCreatives = () => {
        const { appState } = AppData as AppSetup;
        const cache = appState?.getApp()?.bugAdsCache;
        if (cache?.status === 'ready' && cache.adObj) {
            this.adObj = cache.adObj;
        } else {
            this.adObj = null;
            if (cache && (cache.status === 'idle' || cache.status === 'error')) {
                VastParser.prefetch();
            }
        }
    }


    init = (videoInfo: any) => {
        Log.info('adlog', 'BugAds.init() called', '');
        const { appState } = AppData as AppSetup;

        if (!this.config || $.isEmptyObject(this.config)) {
            return false;
        }
        if (this.config.bugadenabled !== true && this.config.bugadenabled !== "true" as any) {
            return false;
        }

        const player = appState.player;
        const firstBugAd = this.config.firstbugadinterval;
        const bugAdInterval = this.config.bugadInterval;
        const currentVideoNo = player.noVideosStarted;
        const isReplay = this.previousVideoId == videoInfo.node_id;

        if (currentVideoNo <= firstBugAd) {
            Log.info('adlog', 'BugAds.init() returning - as firstbugadinterval not reached yet!', '');
            return false;
        }

        if (currentVideoNo == (firstBugAd + 1) || isReplay) {
            this.shouldDisplayAd = true;
        } else if (player.noVideosSinceLastBugAd >= (bugAdInterval)) {
            this.shouldDisplayAd = true;
        } else if (player.videoStartedFromResumePoint !== 0 && isReplay) {
            this.shouldDisplayAd = true;
        } else {
            this.shouldDisplayAd = false;
        }

        if (this.shouldDisplayAd) {
            player.noVideosSinceLastBugAd = 0;
            this.videoRuntime = videoInfo.runtime;
            this.introEndTime = videoInfo.intro_et > 0 ? videoInfo.intro_et : 0;
        } else {
            player.noVideosSinceLastBugAd++;
        }
        Log.info('adlog', 'BugAds.init() shouldDisplayAd:', this.shouldDisplayAd);
        return this.shouldDisplayAd;
    }

    // Schedule the next bug ad one bugadrepeatinterval after the given time (the
    // previous ad's END), as long as it still fits before the last 30s of the
    // video. Because the previous ad's end = its start + its own creative
    // duration, consecutive ads are spaced by bugadrepeatinterval + that ad's
    // duration. Sets nextAdTime = -1 when no further ad fits.
    scheduleNextAd = (afterTime: number) => {
        if (!this.config) return;
        this.pendingInterval = this.config.bugadrepeatinterval;
        const next = afterTime + this.config.bugadrepeatinterval;
        if ((this.videoRuntime - next) > 30) {
            this.nextAdTime = next;
            Log.info('adlog', 'BugAds.scheduleNextAd() - next bug ad at ' + next, '');
        } else {
            this.nextAdTime = -1;
            Log.info('adlog', 'BugAds.scheduleNextAd() - no more bug ads (near end)', '');
        }
    }

    handleAdDisplay = (curTime: number) => {
        const { appState } = AppData as AppSetup;
        const app = appState?.getApp();
        let ads = app?.ads;

        if (this.startTime >= 0 && curTime < (this.startTime - 2)) {
            Log.info('adlog', 'BugAds.handleAdDisplay() - backward seek, re-initializing schedule at', curTime);
            if (this.adDisplayedStatus) {
                this.toggleAdDisplay(this.currbreakPointAdMapCounter, 'hide');
                this.adDisplayedStatus = false;
                if (this.videoPlayer && typeof this.videoPlayer.restoreCCAfterBugAd === 'function') {
                    this.videoPlayer.restoreCCAfterBugAd();
                }
            }
            this.startTime = -1;
            this.nextAdTime = -1;
            this.currentBugAdEndTime = 0;
            this.resetTrackerStatus();
        }

        if (this.shouldDisplayAd == true && this.startTime < 0 && this.config) {
            this.startTime = curTime;
            
            let first = (this.introEndTime > 0 ? this.introEndTime : curTime) + this.config.bugadstartinterval;
            if (first <= curTime) {
                first = curTime + this.config.bugadstartinterval;
            }
            this.pendingInterval = this.config.bugadstartinterval;
            this.nextAdTime = ((this.videoRuntime - first) > 30) ? first : -1;
            Log.info('adlog', 'BugAds.handleAdDisplay() - first bug ad scheduled at ' + this.nextAdTime, '');
        }

        if (ads?.midAdPlaying == true) {
            if (this.adDisplayedStatus) {
                this.dismissAd();
            }
            return;
        }

        if (this.shouldDisplayAd == false || this.adObj == null || !this.adObj.Creatives?.length
                // || helper.isCurrentDepth(DEPTH_MAP.SEARCH)
                // || helper.isCurrentDepth(DEPTH_MAP.VIDEO_PLAYLIST)
                // || helper.isCurrentDepth(DEPTH_MAP.MORE_INFO)
                // || helper.isCurrentDepth(DEPTH_MAP.VIDEO_CONTROL)
            ) {
            return;
        }

        const overlaysVisible = this.isPlayerOverlayVisible();
        if (this.adDisplayedStatus == false && this.nextAdTime >= 0 && overlaysVisible && this.config) {
            const delayed = curTime + this.pendingInterval;
            if (delayed > this.nextAdTime) {
                this.nextAdTime = ((this.videoRuntime - delayed) > 30) ? delayed : -1;
                Log.info('adlog', 'BugAds.handleAdDisplay() - overlay visible, deferring bug ad to ' + this.nextAdTime, '');
            }
        }

        if (this.shouldDisplayAd == true
            && this.nextAdTime >= 0
            && curTime >= this.nextAdTime
            && this.adDisplayedStatus == false
            && overlaysVisible == false
            && this.config
        ) {
            const creativesLen = this.adObj?.Creatives?.length || 0;
            if (ads && ads.breakPointAdMapCounter !== undefined && creativesLen > 0
                && ads.breakPointAdMapCounter >= creativesLen) {
                ads.breakPointAdMapCounter = 0;
            }
            this.currbreakPointAdMapCounter = ads?.breakPointAdMapCounter || 0;

            Log.info('adlog', 'BugAds.handleAdDisplay() - showing bug ad at', curTime);

            this.adDisplayedStatus = true;

            if (ads && ads.breakPointAdMapCounter !== undefined && creativesLen > 0) {
                ads.breakPointAdMapCounter = (this.currbreakPointAdMapCounter + 1 >= creativesLen)
                    ? 0
                    : this.currbreakPointAdMapCounter + 1;
            }

            if (this.videoPlayer && typeof this.videoPlayer.hideOverlaysForBugAd === 'function') {
                this.videoPlayer.hideOverlaysForBugAd(this.isLBandAd(this.currbreakPointAdMapCounter));
            }

            const creative = this.adObj?.Creatives?.[this.currbreakPointAdMapCounter];
            this.currentAdDuration = creative?.CreativeData?.duration ?? this.config.bugadduration;
            this.currentAdPos = curTime;
            this.currentBugAdEndTime = curTime + this.currentAdDuration;
            this.nextAdTime = -1; // rescheduled once this ad ends
            this.resetTrackerStatus();
            this.toggleAdDisplay(this.currbreakPointAdMapCounter, 'show');

            this.fireTrackers(curTime);
            if (this.isLBandAd(this.currbreakPointAdMapCounter)) {
                Logger.processUserAction('LBandAd_Start');
            } else {
                Logger.processUserAction('BugAd_Start');
            }

        } else if (this.shouldDisplayAd == true
            && (curTime >= this.currentBugAdEndTime)
            && this.adDisplayedStatus == true
        ) { // hide ad
            Log.info('adlog', 'BugAds.handleAdDisplay() - hide ad', curTime);
            this.fireTrackers(curTime);
            this.toggleAdDisplay(this.currbreakPointAdMapCounter, 'hide');
            this.adDisplayedStatus = false;
            if (this.videoPlayer && typeof this.videoPlayer.restoreCCAfterBugAd === 'function') {
                this.videoPlayer.restoreCCAfterBugAd();
            }
            this.scheduleNextAd(this.currentBugAdEndTime);
            if (this.isLBandAd(this.currbreakPointAdMapCounter)) {
                Logger.processUserAction('LBandAd_End');
            } else {
                Logger.processUserAction('BugAd_End');
            }

        } else if (this.adDisplayedStatus == true) {
            this.fireTrackers(curTime);
        } else {
            return;
        }

    }

    resetTrackerStatus = () => {
        this.trackersStatus.start = false;
        this.trackersStatus.midpoint = false;
        this.trackersStatus.complete = false;
    }

    // True when the creative at the given index is an L-band. The type comes off
    // the action the ad server serves with it (see VastParser.L_BAND_ACTIONS),
    // resolved once at parse time.
    isLBandAd = (i: number): boolean => {
        return this.adObj?.Creatives?.[i]?.isLBand === true;
    }

    /**
     * The <video> element an L-band shrink applies to. The player's own reference
     * is preferred; the shared DOM node is used as a fallback so a teardown that
     * runs after the page dropped its reference can still restore the size.
     */
    getPlayerElement = (): HTMLElement | null => {
        return (this.videoPlayer?.videoEl as HTMLElement) || document.getElementById('content-player');
    }

    /** The caption box and its text node, both built alongside the player. */
    getSubtitleElement = (): HTMLElement | null => document.getElementById('subtitle');
    getSubtitleTextElement = (): HTMLElement | null =>
        document.querySelector('#subtitle span') as HTMLElement | null;

    /**
     * Animate the layout change that follows, then drop the transitions again once
     * it has run: both elements are shared by every screen, so a lingering
     * transition would also animate changes that are meant to be instant (page
     * setup, grid autoplay preview). The layout read commits the current values
     * first - without it the new ones can land in the same style pass and the
     * transition never starts on the older WebKit builds these TVs ship.
     */
    armTransition = (els: (HTMLElement | null)[]) => {
        this.setTransition(els, L_BAND_TRANSITION);
        void document.body.offsetWidth;
        if (this.transitionTimerId) {
            clearTimeout(this.transitionTimerId);
        }
        this.transitionTimerId = setTimeout(() => {
            this.transitionTimerId = null;
            this.setTransition(els, '');
        }, L_BAND_TRANSITION_MS);
    }

    setTransition = (els: (HTMLElement | null)[], value: string) => {
        els.forEach(el => {
            if (!el) return;
            const style = el.style as any;
            style.transition = value;
            style.webkitTransition = value;
        });
    }

    /**
     * Put the player - and the captions that belong to it - into `rect`. Both states
     * the L-band toggles between are the same calculation with a different rect, so
     * one method covers them: the player is sized with Helper.resizePlayer, and the
     * caption box is re-derived the same way, by measuring SUBTITLE_INSET against
     * `rect` instead of the whole screen and scaling it by how much the player
     * shrank. FULL_SCREEN reproduces the element's original insets exactly, so the
     * restore is this call with the other rect.
     *
     * The offsets are written as plain left/right/bottom rather than a CSS
     * transform: a transform puts the element in its own stacking context and
     * compositing layer, which is what goes wrong around a hardware-composited
     * video on these devices.
     */
    applyPlayerLayout = (rect: PlayerRect) => {
        const videoEl = this.getPlayerElement();
        const subtitle = this.getSubtitleElement();
        this.armTransition([videoEl, subtitle, this.getSubtitleTextElement()]);

        Helper.resizePlayer(
            videoEl,
            this.toPercent(rect.x, DESIGN_FRAME.width),
            this.toPercent(rect.y, DESIGN_FRAME.height),
            this.toPercent(rect.width, DESIGN_FRAME.width),
            this.toPercent(rect.height, DESIGN_FRAME.height)
        );

        if (!subtitle) return;

        // The player is sized in percentages, so it takes the same fractions of
        // whatever the panel actually reports - work the caption box out in those
        // same device pixels.
        const scaleX = rect.width / DESIGN_FRAME.width;
        const scaleY = rect.height / DESIGN_FRAME.height;
        const videoX = window.innerWidth * (rect.x / DESIGN_FRAME.width);
        const videoY = window.innerHeight * (rect.y / DESIGN_FRAME.height);
        const videoW = window.innerWidth * scaleX;
        const videoH = window.innerHeight * scaleY;

        subtitle.style.left = Math.round(videoX + SUBTITLE_INSET.left * scaleX) + 'px';
        subtitle.style.right = Math.round((window.innerWidth - videoX - videoW)
            + SUBTITLE_INSET.right * scaleX) + 'px';
        subtitle.style.bottom = Math.round((window.innerHeight - videoY - videoH)
            + SUBTITLE_INSET.bottom * scaleY) + 'px';
        subtitle.style.maxWidth = Math.round(videoW
            - (SUBTITLE_INSET.left + SUBTITLE_INSET.right) * scaleX) + 'px';
        // Lift the captions past the creative while the band is up, and hand the
        // stacking back afterwards. Without this they land underneath the band
        // artwork and only show if it happens to be transparent right there.
        subtitle.style.zIndex = this.lBandActive ? String(BUG_AD_Z_INDEX + 1) : '';
        this.scaleSubtitleFont(scaleX);
    }

    /**
     * Scale the caption text with the video. The size is a user setting owned by
     * CCConfig.updateCSS, so what it wrote is stashed and scaled - colour, opacity,
     * edge and background are left alone - and put back verbatim at full screen.
     */
    scaleSubtitleFont = (scale: number) => {
        const span = this.getSubtitleTextElement();
        if (!span) return;
        if (this.subtitleFontSize === null) {
            this.subtitleFontSize = span.style.fontSize;
        }
        if (scale === 1) {
            span.style.fontSize = this.subtitleFontSize;
            this.subtitleFontSize = null;
            return;
        }
        const fullSize = parseInt(this.subtitleFontSize, 10);
        if (fullSize) {
            span.style.fontSize = Math.round(fullSize * scale) + 'px';
        }
    }

    /**
     * Scale the video into the corner given by L_BAND_PLAYER so the L-band creative
     * has the edges it occupies to itself.
     */
    shrinkPlayerForLBand = () => {
        if (this.lBandActive) return;
        this.lBandActive = true;
        this.applyPlayerLayout(L_BAND_PLAYER);
        // Log.info('adlog', 'BugAds.shrinkPlayerForLBand() - player shrunk to '
        //     + L_BAND_PLAYER.width + 'x' + L_BAND_PLAYER.height, '');
    }

    /** Give the whole screen back to the video once the L-band is gone. */
    restorePlayerFromLBand = () => {
        if (!this.lBandActive) return;
        this.lBandActive = false;
        this.applyPlayerLayout(FULL_SCREEN);
        // Log.info('adlog', 'BugAds.restorePlayerFromLBand() - player restored to full screen', '');
    }

    /** Express a design-frame pixel value as a percentage of that frame. */
    toPercent = (value: number, total: number): number => {
        return Math.round((value / total) * 10000) / 100;
    }

    // True when any player overlay a bug ad must not cover is on screen. The
    // video player owns those components, so we delegate the check to it; if the
    // player can't answer, treat the screen as clear so ads aren't blocked forever.
    isPlayerOverlayVisible = (): boolean => {
        if (this.videoPlayer && typeof this.videoPlayer.isPlayerOverlayVisible === 'function') {
            return this.videoPlayer.isPlayerOverlayVisible() === true;
        }
        return false;
    }

    /**
     * Perform the action attached to the currently-displayed bug-ad creative.
     * addToFavorite (the L-band CTA) keeps the user on the current video and only
     * updates their watchlist. The rest target another site: launchInAppOpening
     * opens that site's main grid, openViaDeeplink opens it at a specific playId,
     * launchChannelStore is reserved (no-op, as in legacy). For a real launch we
     * remove the ad and tear the video player down immediately (see
     * leaveForChildApp) rather than waiting for the async child-app load.
     */
    executeActions = () => {
        const { appConfig } = AppData as AppSetup;
        if (appConfig?.nonClickableBugAds) {
            Log.info('PlatformBase Vizio: nonClickableBugAds ', appConfig?.nonClickableBugAds);
            return
        }
        const idx = this.currbreakPointAdMapCounter;
        const currentAd = this.adObj?.Creatives?.[idx];
        if (!currentAd || typeof currentAd.actions === 'undefined') {
            return;
        }

        const type = Object.keys(currentAd.actions)[0];
        Log.info('info', 'BugAds.executeActions() - type: ', type);

        if (VastParser.isLBandAction(currentAd.actions)) {
            Logger.processUserAction('LBandAd_Action_' + type);
            this.dismissAd(true);
            return;
        }

        let siteId: any;
        let playId: string | null = null;
        if (type === 'launchChannelStore') {
            siteId = currentAd.actions.launchChannelStore?.siteid;
        } else if (type === 'launchInAppOpening') {
            siteId = currentAd.actions.launchInAppOpening?.siteid;
        } else if (type === 'openViaDeeplink') {
            siteId = currentAd.actions.openViaDeeplink?.siteid;
            playId = currentAd.actions.openViaDeeplink?.deeplinkParameter ?? null;
        }

        Logger.processUserAction('BugAd_Action_' + type, siteId);

        if (!Helper.isValidValue(siteId) || siteId == 0) {
            Log.info('error', 'BugAds.executeActions() - no site id / invalid site id', siteId);
            // this.dismissAd();
            return;
        }

        switch (type) {
            case 'launchChannelStore':
                // Reserved for external app-store launch; no-op as in legacy.
                break;
            case 'launchInAppOpening':
                this.leaveForChildApp();
                childApp.loadChildApp({ site_id: String(siteId) });
                break;
            case 'openViaDeeplink':
                this.leaveForChildApp();
                childApp.loadChildApp({ site_id: String(siteId) }, playId);
                break;
        }
    }

    /**
     * Close the currently-shown bug ad without launching anything and hand
     * control back to the video (which is still playing underneath): remove the
     * overlay, clear the displayed flag, restore closed captions and return
     * focus to the player. Used when a clicked ad has no valid site id.
     */
    dismissAd = (isLBandAd: boolean = false) => {
        this.toggleAdDisplay(this.currbreakPointAdMapCounter, 'hide');
        this.adDisplayedStatus = false;
        if (this.videoPlayer && typeof this.videoPlayer.restoreCCAfterBugAd === 'function') {
            this.videoPlayer.restoreCCAfterBugAd();
        }
        this.scheduleNextAd(this.currentBugAdEndTime);
        if (isLBandAd) {
            Logger.processUserAction('LBandAd_End');
        } else {
            Logger.processUserAction('BugAd_End');
        }
    }

    /**
     * Remove the bug ad overlay and tear down the video player before launching
     * the child app. loadChildApp's loadIndex() is async, and Router navigation
     * alone does not stop the video, so we do it explicitly here: closeVideo()
     * deinits this BugAds (removing #bug-ads-container) and destroys the player
     * instance/listeners. Falls back to just hiding the ad if unavailable.
     */
    leaveForChildApp = () => {
        if (this.videoPlayer && typeof this.videoPlayer.closeVideo === 'function') {
            this.videoPlayer.closeVideo();
        } else {
            this.toggleAdDisplay(this.currbreakPointAdMapCounter, 'hide');
            this.adDisplayedStatus = false;
        }
    }

    toggleAdDisplay = (i: number, type: string) => {
        Log.info('adlog', 'BugAds.toggleAdDisplay() - BugAds-Index: ' + i, type);
        if (type == 'show') {
            if (this.adObj && this.adObj.Creatives && this.adObj.Creatives[i] !== undefined) {
                var crt = this.adObj.Creatives[i].CreativeData;
                if (crt !== undefined) {
                    var imgUrl = crt.url;

                    if (this.isLBandAd(i)) {
                        this.shrinkPlayerForLBand();
                    }

                    const scaleX = window.innerWidth  / (crt.refWidth  ?? 1920);
                    const scaleY = window.innerHeight / (crt.refHeight ?? 1080);

                    const scaledWidth = crt.width ? Math.round(crt.width * scaleX) : 0;
                    const scaledHeight = crt.height ? Math.round(crt.height * scaleY) : 0;
                    const top = crt.ypos !== undefined ? Math.round(crt.ypos * scaleY) + 'px' : Math.round(20 * scaleY) + 'px';
                    const left = crt.xpos !== undefined ? Math.round(crt.xpos * scaleX) + 'px' : 'auto';
                    const right = crt.xpos !== undefined ? 'auto' : Math.round(20 * scaleX) + 'px';

                    var styles = 'style="position:absolute; left: ' + left + ';'
                        + 'top: ' + top + ';'
                        + 'right: ' + right + ';'
                        + 'z-index: ' + BUG_AD_Z_INDEX + ';'
                        + (scaledHeight ? 'height: ' + scaledHeight + 'px;' : '')
                        + (scaledWidth ? 'width: ' + scaledWidth + 'px;' : '')
                        + '"';
                    var stylesImg = 'style="'
                        + (scaledHeight ? 'height: ' + scaledHeight + 'px;' : '')
                        + (scaledWidth ? 'width: ' + scaledWidth + 'px;' : '')
                        + '"';
                    var adHtml = '<div id="bug-ads-container" ' + styles + '><img ' + stylesImg + ' src="' + imgUrl + '"></div>';
                    
                    $('body').append(adHtml);
                    $('#bug-ads-container').fadeIn(500);
                    $('#bug-ads-container').on('click', () => {
                        Logger.processUserAction('BugAd_Click');
                        this.executeActions();
                    });

                    this.initAdLog(true, true);
                } else {
                    this.initAdLog(false, false);
                }
            } else {
                this.initAdLog(false, false);
            }
        } else {
            this.restorePlayerFromLBand();
            $('#bug-ads-container').fadeOut(500, function() {
                $(this).remove();
            });
        }
    }

    fireTrackers = (curTime: number) => {
        if (this.adDisplayedStatus == true && this.config) {
            var timeElapsed = (curTime - this.currentAdPos);

            const creative = this.adObj?.Creatives?.[this.currbreakPointAdMapCounter];

            if ((timeElapsed <= 1)
                && this.trackersStatus.start == false
            ) {
                Log.info('adlog', 'BugAds.fireTrackers() - start tracker', '');
                this.trackersStatus.start = true;
                if (this.adObj?.Impression) {
                    this.pingTracker(this.adObj.Impression);
                }
                if (creative?.Impression) {
                    this.pingTracker(creative.Impression);
                }
                if (creative?.trackers?.start) {
                    this.pingTracker(creative.trackers.start);
                }
            } else if ((timeElapsed >= (this.currentAdDuration / 2))
                && this.trackersStatus.midpoint == false
            ) {
                Log.info('adlog', 'BugAds.fireTrackers() - midpoint tracker', '');
                this.trackersStatus.midpoint = true;
                if (creative?.trackers?.midpoint) {
                    this.pingTracker(creative.trackers.midpoint);
                }
            } else if ((timeElapsed >= this.currentAdDuration)
                && this.trackersStatus.complete == false
            ) {
                Log.info('adlog', 'BugAds.fireTrackers() - complete tracker', '');
                this.trackersStatus.complete = true;
                if (creative?.trackers?.complete) {
                    this.pingTracker(creative.trackers.complete);
                }
            }
        } else {
            return;
        }
    }

    pingTracker = (url: string): void => {
        fetch(url).catch(() => {
            Log.info('adlog', 'BugAds.pingTracker() - failed', url);
        });
    }

    initAdLog = (adAvailable: boolean, adPlayed: boolean) => {
        var adDetails: any = {};
        adDetails.adProvider = 'futuretodayinc';
        adDetails.adPriority = this.config?.adList?.priority || 0;
        adDetails.adAvailable = adAvailable;
        adDetails.adPlayed = adPlayed;
        adDetails.adType = 'popupad';
        adDetails.transactionId = Helper.guid();

        if (adAvailable == true && this.adObj) {
            if (this.adObj.AdTitle == undefined) {
                adDetails.adTitle = 'Bug Ad';
            } else {
                adDetails.adTitle = this.adObj.AdTitle;
            }

            if (this.adObj.adId == undefined) {
                adDetails.adId = 1;
            } else {
                adDetails.adId = this.adObj.adId;
            }
        }

        Logger.logAdView(adDetails);
    }

    deInit = () => {
        Log.info('adlog', 'BugAds.deInit() - called', '');
        this.shouldDisplayAd = false;
        this.adDisplayedStatus = false;
        this.adObj = null;
        this.trackersStatus = {
            start: false,
            midpoint: false,
            complete: false
        };
        this.toggleAdDisplay(this.currbreakPointAdMapCounter, 'hide');
        $('head link[data-bugad="true"]').remove();
    }
}
